import {
    keepPreviousData,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query"
import * as businessApi from "@/api/endpoints/business/business"
import { resolveEnabled, type QueryOptions } from "@/queries/query-helpers"

export const businessKeys = {
    all: ["business"] as const,
    detail: (id: string) => [...businessKeys.all, "detail", id] as const,
    appointments: (params: {
        page: number
        pageSize: number
        status?: string | string[]
        search?: string
        business?: string
        service?: string
        customer?: string
    }) =>
        [...businessKeys.all, "appointments", params] as const,
    appointment: (id: string) => [...businessKeys.all, "appointment", id] as const,
    incomeMetrics: (id: string) =>
        [...businessKeys.all, "incomeMetrics", id] as const,
    dashboard: (id: string) => [...businessKeys.all, "dashboard", id] as const,
}

export const useGetAppointmentsQuery = (
    params: {
        page: number
        pageSize: number
        status?: string | string[]
        search?: string
        business?: string
        service?: string
        customer?: string
    },
    options?: QueryOptions
) =>
    useQuery({
        queryKey: businessKeys.appointments(params),
        queryFn: () => businessApi.getAppointments(params),
        placeholderData: keepPreviousData,
        enabled: resolveEnabled(options),
    })

export const useGetAppointmentQuery = (
    appointmentId: string,
    options?: QueryOptions
) =>
    useQuery({
        queryKey: businessKeys.appointment(appointmentId),
        queryFn: () => businessApi.getAppointment(appointmentId),
        enabled: resolveEnabled(options) && !!appointmentId,
    })

export const useGetAppointmentNotesQuery = (
    appointmentId: string,
    options?: QueryOptions
) =>
    useQuery({
        queryKey: [...businessKeys.appointment(appointmentId), "notes"] as const,
        queryFn: () => businessApi.getAppointmentNotes(appointmentId),
        enabled: resolveEnabled(options) && !!appointmentId,
    })

export const useGetIncomeManagementMetricsQuery = (
    params: { id: string },
    options?: QueryOptions
) =>
    useQuery({
        queryKey: businessKeys.incomeMetrics(params.id),
        queryFn: () => businessApi.getIncomeManagementMetrics(params),
        enabled: resolveEnabled(options),
    })

export const useGetBusinessQuery = (id: string, options?: QueryOptions) =>
    useQuery({
        queryKey: businessKeys.detail(id),
        queryFn: () => businessApi.getBusiness(id),
        enabled: resolveEnabled(options) && !!id,
    })

export const useCreateBusinessMutation = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: businessApi.createBusiness,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: businessKeys.all })
            queryClient.invalidateQueries({ queryKey: ["user", "profile"] })
        },
    })
}

export const useUpdateBusinessMutation = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: businessApi.updateBusiness,
        onSuccess: (_, variables) => {
            if (variables && typeof variables === "object" && "id" in variables) {
                const id = String((variables as { id: string }).id)
                queryClient.invalidateQueries({ queryKey: businessKeys.detail(id) })
            }
            queryClient.invalidateQueries({ queryKey: ["user", "profile"] })
        },
    })
}

export const usePromoteBusinessToParentMutation = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: businessApi.promoteBusinessToParent,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: businessKeys.all })
            queryClient.invalidateQueries({ queryKey: ["user", "profile"] })
        },
    })
}

export const useRequestPayoutMutation = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: businessApi.requestPayout,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: businessKeys.all })
        },
    })
}

export const useAddBusinessBankAccountMutation = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: businessApi.addBusinessBankAccount,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: businessKeys.all })
        },
    })
}

export const useRescheduleAppointmentMutation = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: businessApi.rescheduleAppointment,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: businessKeys.all })
        },
    })
}

const invalidateAppointments = (queryClient: ReturnType<typeof useQueryClient>, id?: string) => {
    queryClient.invalidateQueries({ queryKey: businessKeys.all })
    if (id) {
        queryClient.invalidateQueries({ queryKey: businessKeys.appointment(id) })
    }
}

export const useCancelAppointmentMutation = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: businessApi.cancelAppointment,
        onSuccess: (_, variables) => invalidateAppointments(queryClient, variables.appointment),
    })
}

export const useCompleteAppointmentMutation = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: businessApi.completeAppointment,
        onSuccess: (_, variables) => invalidateAppointments(queryClient, variables.appointment),
    })
}

export const useMarkAppointmentInProgressMutation = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: businessApi.markAppointmentInProgress,
        onSuccess: (_, variables) => invalidateAppointments(queryClient, variables.appointment),
    })
}

export const useAcceptRescheduleRequestMutation = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: businessApi.acceptRescheduleAppointmentRequest,
        onSuccess: (_, variables) => invalidateAppointments(queryClient, variables.appointment),
    })
}

export const useCancelRescheduleRequestMutation = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: businessApi.cancelRescheduleAppointmentRequest,
        onSuccess: (_, variables) => invalidateAppointments(queryClient, variables.appointment),
    })
}

export const useInitiateAdditionalFeeMutation = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: businessApi.initiateAdditionalFee,
        onSuccess: (_, variables) => invalidateAppointments(queryClient, variables.appointmentId),
    })
}

export const useCreateAppointmentNoteMutation = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: businessApi.createAppointmentNote,
        onSuccess: (_, variables) => invalidateAppointments(queryClient, variables.appointmentId),
    })
}

export const useImportBusinessesViaExcelMutation = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: businessApi.importBusinessesViaExcel,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: businessKeys.all })
            queryClient.invalidateQueries({ queryKey: ["user", "profile"] })
        },
    })
}

export const useGetBusinessDashboardQuery = (
    businessId: string,
    options?: QueryOptions
) =>
    useQuery({
        queryKey: businessKeys.dashboard(businessId),
        queryFn: () => businessApi.getBusinessDashboard({ business: businessId }),
        enabled: resolveEnabled(options) && !!businessId,
    })

export const useGetAvailableSlotsMutation = () =>
    useMutation({
        mutationFn: businessApi.getAvailableSlots,
    })

export const useBookAppointmentOnBehalfMutation = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: businessApi.bookAppointmentOnBehalf,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: businessKeys.all })
            queryClient.invalidateQueries({ queryKey: ["customers"] })
        },
    })
}
